import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import brand from '../theme/brand';
import { IOS_APP_STORE_URL, ANDROID_PLAY_STORE_URL } from '../constants/appStores';
import { detectPlatform, isPhonePlatform } from '../utils/platform';

interface Props {
  code: string;
  /** Full https://…/a/<code> URL, encoded in the QR on non-phone browsers. */
  url: string;
}

/** The two steps shared by the chat sheet and the /a/:code page:
    1. Get the app  2. Add this conversation (code, QR on computers). */
const AppHandoffSteps: React.FC<Props> = ({ code, url }) => {
  const platform = detectPlatform();
  const phone = isPhonePlatform(platform);
  const [showBoth, setShowBoth] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — the code is on screen
    }
  };

  const openStore = (storeUrl: string) => {
    if (phone) {
      // Best effort: have the code on the clipboard when they come back.
      navigator.clipboard?.writeText(code).catch(() => undefined);
    }
    window.open(storeUrl, '_blank', 'noopener');
  };

  const showIos = platform === 'ios' || platform === 'other' || showBoth;
  const showAndroid = platform === 'android' || platform === 'other' || showBoth;

  const storeButtonSx = {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 999,
    py: 1.1,
    background: brand.forest,
    color: brand.cream,
    boxShadow: 'none',
    '&:hover': { background: brand.forestDeep, boxShadow: 'none' },
  } as const;

  const stepLabelSx = { fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: brand.sage, mb: 0.75 } as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'left' }}>
      <Box>
        <Typography sx={stepLabelSx}>1. Get the app</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {showIos && (
            <Button variant="contained" fullWidth onClick={() => openStore(IOS_APP_STORE_URL)} sx={storeButtonSx}>
              Download on the App Store
            </Button>
          )}
          {showAndroid && (
            <Button variant="contained" fullWidth onClick={() => openStore(ANDROID_PLAY_STORE_URL)} sx={storeButtonSx}>
              Get it on Google Play
            </Button>
          )}
        </Box>
        {phone && !showBoth && (
          <Button
            variant="text"
            size="small"
            onClick={() => setShowBoth(true)}
            sx={{ mt: 0.5, textTransform: 'none', color: brand.warmGray, p: 0, minWidth: 0 }}
          >
            Different phone?
          </Button>
        )}
      </Box>

      <Box>
        <Typography sx={stepLabelSx}>2. Add this conversation</Typography>
        {!phone && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Box sx={{ p: 1.5, background: '#FFFFFF', borderRadius: '12px', border: `1px solid ${brand.border}`, lineHeight: 0 }}>
              <QRCodeSVG value={url} size={148} fgColor={brand.forest} bgColor="#FFFFFF" level="M" />
            </Box>
            <Typography variant="caption" sx={{ mt: 0.75, color: brand.warmGray }}>
              Scan with your phone's camera
            </Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: 15, color: brand.ink, mb: 1 }}>
          Open Paiper, tap <b>Add conversation</b> and enter
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            aria-label={`Code ${code.split('').join(' ')}`}
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: '0.18em',
              color: brand.forest,
              background: brand.lightSage,
              borderRadius: '12px',
              px: 2,
              py: 0.75,
              userSelect: 'all',
            }}
          >
            {code}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={copyCode}
            sx={{ textTransform: 'none', borderRadius: 999, borderColor: brand.sage, color: brand.forest, fontWeight: 600 }}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AppHandoffSteps;
